import { useState } from 'react';
import { QrCode, Download, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API = '/api';

const QRCodeModal = ({ business, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  
  const qrUrl = `${API}/businesses/${business?.id}/qr?base_url=${encodeURIComponent(window.location.origin)}`;

  const handleDownload = async () => {
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${business.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  if (!business) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" aria-describedby="qr-dialog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-orange-500" />
            QR Code
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center py-4">
          <p id="qr-dialog-description" className="sr-only">
            QR code for {business.name}. Scan to view this business.
          </p>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-border mb-4">
            {loading && (
              <div className="w-48 h-48 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <img
              src={qrUrl}
              alt={`QR code for ${business.name}`}
              className={`w-48 h-48 ${loading ? 'hidden' : ''}`}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          </div>
          
          <h3 className="font-semibold text-center mb-1">{business.name}</h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Scan to view this business
          </p>
          
          <Button onClick={handleDownload} className="w-full">
            <Download className="w-4 h-4 mr-2" />
            Download QR Code
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Trigger button component
export const QRCodeButton = ({ business, size = 'default' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const sizeClasses = {
    small: 'p-1.5',
    default: 'p-2',
    large: 'p-3'
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
        className={`${sizeClasses[size]} rounded-lg text-muted-foreground hover:text-orange-500 hover:bg-orange-50 transition-colors`}
        aria-label="Show QR code"
        data-testid={`qr-btn-${business.id}`}
      >
        <QrCode className="w-5 h-5" />
      </button>
      
      <QRCodeModal 
        business={business} 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
      />
    </>
  );
};

export default QRCodeModal;
